import { useEffect, useState } from 'react'

import { AnimatedNumberProps } from './types'

const DEFAULT_DURATION = 900

const easeOutCubic = (progress: number): number => {
  const inverse = 1 - progress
  return 1 - inverse * inverse * inverse
}

/**
 * Counts up (or down) from the previously rendered value to the target
 * value over `durationMs` using a cubic ease-out. Falls back instantly
 * when the user prefers reduced motion.
 *
 * @param {AnimatedNumberProps} props
 * @returns {JSX.Element}
 */
const AnimatedNumber = ({
  value,
  durationMs = DEFAULT_DURATION,
  formatter,
  prefix = '',
  suffix = '',
}: AnimatedNumberProps): JSX.Element => {
  const [displayValue, setDisplayValue] = useState<number>(value)

  useEffect(() => {
    if (typeof window === 'undefined') {
      setDisplayValue(value)
      return
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion === true) {
      setDisplayValue(value)
      return
    }

    const startValue = displayValue
    const delta = value - startValue

    if (delta === 0) {
      return
    }

    const startTime = performance.now()
    let frameHandle = 0

    const step = (now: number): void => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / durationMs, 1)
      const eased = easeOutCubic(progress)
      const next = startValue + delta * eased
      setDisplayValue(next)

      if (progress < 1) {
        frameHandle = requestAnimationFrame(step)
      } else {
        setDisplayValue(value)
      }
    }

    frameHandle = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(frameHandle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs])

  const renderedNumber = formatter
    ? formatter(displayValue)
    : Math.round(displayValue).toString()

  return (
    <span>
      {prefix}
      {renderedNumber}
      {suffix}
    </span>
  )
}

export default AnimatedNumber
